import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const IndustryBreakdown = ({ entityType = "", compact = false }) => {
  const [dist, setDist] = useState(null);
  const [aicci, setAicci] = useState(null);
  const [view, setView] = useState("distribution");

  useEffect(() => {
    const params = entityType ? `?entity_type=${entityType}` : "";
    Promise.allSettled([
      axios.get(`${API}/industry/distribution${params}`),
      axios.get(`${API}/industry/aicci${params}`),
    ]).then(([d, a]) => {
      if (d.status === "fulfilled") setDist(d.value.data);
      if (a.status === "fulfilled") setAicci(a.value.data);
    });
  }, [entityType]);

  if (!dist) return null;

  const chartData = view === "distribution"
    ? dist.distribution.slice(0, compact ? 8 : 16).map(d => ({ name: d.industry.replace(" & ", "\n& "), value: d.count, color: d.color, fullName: d.industry }))
    : (aicci || []).slice(0, compact ? 8 : 16).map(d => ({ name: d.industry.replace(" & ", "\n& "), value: d.aicci, color: d.color, fullName: d.industry, care: d.care_pct, extraction: d.extraction_pct }));

  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid="industry-breakdown">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold">Industry Clusters</h3>
        <div className="flex items-center gap-1 bg-white/[0.04] rounded-lg p-0.5">
          <button onClick={() => setView("distribution")}
            className={`text-[10px] px-3 py-1 rounded-md font-medium transition-all ${view === "distribution" ? "bg-white/[0.1] text-white" : "text-slate-400 hover:text-slate-300"}`}>
            Count
          </button>
          <button onClick={() => setView("aicci")}
            className={`text-[10px] px-3 py-1 rounded-md font-medium transition-all ${view === "aicci" ? "bg-white/[0.1] text-white" : "text-slate-400 hover:text-slate-300"}`}>
            AICCI
          </button>
        </div>
      </div>
      <div style={{ height: compact ? 260 : 380 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} />
            <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} stroke="rgba(255,255,255,0.08)"
              tickFormatter={v => view === "aicci" ? `${v}` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
            <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 9 }} width={compact ? 80 : 110} stroke="rgba(255,255,255,0.08)" />
            <Tooltip
              contentStyle={{ background: "#0a1628", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }}
              formatter={(v, _, props) => {
                if (view === "aicci") return [`AICCI: ${v}/100 (Care: ${props.payload.care}%, Extraction: ${props.payload.extraction}%)`, props.payload.fullName];
                return [v.toLocaleString(), props.payload.fullName];
              }}
            />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} animationDuration={800}>
              {chartData.map((e, i) => <Cell key={i} fill={e.color} fillOpacity={0.8} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {view === "aicci" && aicci && (
        <p className="text-[10px] text-slate-500 mt-2 text-center italic">
          Industry AICCI = care orientation weight + founder diversity - extraction penalty
        </p>
      )}
    </div>
  );
};
